import {h, Fragment} from "preact"
import { AutocompleteElement } from "@nosto/preact"
import { productImagePlaceholder, getImageSize, getProductImages, sortSwatches, getMinMaxProductPrice, formatCurrency, getColorSwatchSrc } from "../helpers"
import { useState, useEffect } from "preact/hooks"
import Swatches from "./Swatches"

export default function AutocompleteProduct({ product, hit, primaryFirst = true, salesBoost = false, masterColors = null, filters = {} }) {
  const primaryColors = product.tags1.filter((tag) => {
    return tag.toLowerCase().includes('primary:');
  });
  const swatches = sortSwatches(product.skus, primaryColors, primaryFirst, salesBoost);
  const [ hoverImageFirst, setHoverImageFirst ] = useState(false);
  const defaultVariant = {'name': swatches[0].name.split(' / ')[1].trim(), 'img': swatches[0].imageUrl};
  const defaultImages = getProductImages(product, defaultVariant);
  const [ productImages, setProductImages ] = useState(defaultImages);
  const [ productUrl, setProductUrl ] = useState(product.url);
  const [ productPrices, setProductPrices ] = useState([]);
  const [ pricesForVariants, setPricesForVariants ] = useState([]);
  const product_badges_meta = product?.customFields?.filter((field)=> field.key === 'product_meta-badges')[0]?.value;
  const colors = [];
  let activeFilterColors = null;

  if (filters.filter((filterItem) => filterItem.name.toLowerCase() === 'color').length && masterColors) {
    const filterColors = filters.filter((filterItem) => filterItem.name.toLowerCase() === 'color');
    for (let color in masterColors) {
      const findedColor = filterColors.find(filterColor => filterColor.value.toLowerCase() === color.toLowerCase());
      if (findedColor) {
        activeFilterColors = masterColors[color].split(',');
        activeFilterColors = activeFilterColors.map(activeFilterColor => activeFilterColor.trim());
        break;
      }
    }
  }

  const getBadgeForVariant = (variantId) => {
    const variant = product?.skus?.find((v) => v.id === variantId);
    const badgeMeta = variant?.customFields?.find(
      (field) => field.key === 'variant_meta-badges'
    )?.value;

    try {
      const parsedBadges = JSON.parse(badgeMeta);
      return parsedBadges;
    } catch {
      return null;
    }
  };

  swatches.forEach((swatch) => {
    const colorTitle = swatch.customFields.find(item => item.key === 'color')?.value;
    if (colorTitle && !colors.find(item => item.name === colorTitle)) {
      colors.push({
        id: swatch.id,
        name: colorTitle,
        src: getColorSwatchSrc(colorTitle, '24x'),
        img: swatch.imageUrl
      });
    }
  });
  const [ activeVariant, setActiveVariant] = useState(colors?.[0]?.id);
  const variantSwitch = (variant) => {
    const variantPrices = pricesForVariants.find(price => price.id == variant.id);
    setActiveVariant(variant.id)
    setProductImages(getProductImages(product, variant, hoverImageFirst));
    setProductUrl(`${product.url}?variant=${variant.id}`);

    if (variantPrices) {
      setProductPrices({
        min: variantPrices.min,
        max: variantPrices.max,
        compare_at_price: variantPrices.compare_at_price
      })
    }
  }

  useEffect(() => {
    const searchedProductHoverImage = product.customFields?.filter(field => field.key === "product_meta-show_hover_image_first")?.value;
    
    if (!searchedProductHoverImage || searchedProductHoverImage === 'true') {
      setHoverImageFirst(true);
    }
  }, [product]);

  useEffect(() => {
    if (colors.length > 0 && filters.length) {
      if (filters.filter((filterItem) => filterItem.name.toLowerCase() === "color").length && masterColors && activeFilterColors) {
        let chosenColor;
        if (filters.filter((filterItem) => filterItem.name.toLowerCase() === "color").length && masterColors && activeFilterColors) {
          const filteredColorList = colors.filter((color) => {
            return activeFilterColors.includes(color.name.toLowerCase());
          });
          if (filteredColorList.length) {
            chosenColor = filteredColorList[0];
          } else {
            chosenColor = colors[0];
          }
        }

        if (activeVariant !== chosenColor.id) {
          setActiveVariant(chosenColor.id);
          setProductImages(getProductImages(product, chosenColor, hoverImageFirst));
          setProductUrl(`${product.url}?variant=${chosenColor.id}`);
        }
      }
    } else if (colors.length > 0 && !filters.length) {
      setActiveVariant(colors[0].id);
      setProductImages(getProductImages(product, colors[0], hoverImageFirst));
      setProductUrl(`${product.url}?variant=${colors[0].id}`);
    }
  }, [filters, hoverImageFirst]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${product.url}.js`);
        if (!response.ok) {
          throw new Error(`Error fetching data for item ${product.id}`);
        }
        const result = await response.json();
        const variantsPrices = [];


        result.variants.forEach((variant) => {

          if (variant.id == activeVariant) {
            setProductPrices({
              min: variant.price / 100,
              max: 0,
              compare_at_price: variant.compare_at_price / 100,
            });
          }

          variantsPrices.push({
            id: variant.id,
            min: variant.price / 100,
            max: null,
            compare_at_price: variant.compare_at_price / 100
          });
        });

        setPricesForVariants(variantsPrices);

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);



  const tags = [
    product.onSale
      ? {
        label: "Sale",
        class: "ns-badge ns-badge-green-outline ns-d-inline-block ns-text-align-center"
      }
      : undefined
  ].filter(v => !!v);

  let footerText;

  if (product.tags1.length > 0) {
    const footerTextIndex = product.tags1.findIndex(tag => tag.includes('footertext:') );

    if (footerTextIndex > 0) {
      footerText = product.tags1[footerTextIndex].split(':')[1].trim('/');
    }
  }


  function showProductDetailsEvent() {
    let event = new CustomEvent("showProductDetails", {
      detail: {
        product: product.productId,
        variant: activeVariant
      }
    });

    document.dispatchEvent(event);
  };
  const fallbackBadges = product_badges_meta
  ? JSON.parse(product_badges_meta)
  : [];

  const finalBadge = getBadgeForVariant(activeVariant) || fallbackBadges;
  return (
    <div
      class="ns-product ns-border-box ns-clickable ns-text-undecorated ns-color-inherit ns-col-12 ns-col-xs-6 ns-col-l-3 ns-p-2 ns-my-2 ns-mx-0"
    >
      <div class="ns-product_wrp">
        <div class="ns-relative ns-product-top">
          {Array.isArray(finalBadge) ? (
            <div className="ns-product-badges ns-absolute">
              {finalBadge.map((badge, index) => (
                <div className="ns-product-badge" key={index}>{badge}</div>
              ))}
            </div>
          ) : (
            finalBadge && (
              <div className="ns-product-badges ns-absolute">
                <div className="ns-product-badge">{finalBadge}</div>
              </div>
            )
          )}
          <div class="ns-relative ns-product-img">
            <AutocompleteElement
              as="a"
              hit={{url: productUrl}}
            >
              { productImages?.main && (<img
                class="ns-w-100 ns-h-auto ns-main-image"
                src={productImages.main}
                alt={product.name}
                width="600"
                height="400"
              />) }
              { productImages?.hover && ( <img
                class="ns-w-100 ns-h-auto ns-hover-image"
                src={productImages.hover}
                alt={product.name}
                width="600"
                height="400"
              />) }
            </AutocompleteElement>
          </div>
        </div>
        <div class="ns-relative ns-mt-2 ns-text-align-left ns-text-align-md-center ns-product-bottom" data-nosto-element="product">
          {tags.length > 0 && (
            <div class="ns-tags ns-absolute ns-mb-1">
              {tags.map(v => (
                <span class={v.class}>{v.label}</span>
              ))}
            </div>
          )}
          {/* {product.brand && <div class="ns-color-black ns-mb-1 ns-font-4">{product.brand}</div>} */}
          <div class="ns-swatches">
            {product?.skus.length > 0 && (
              <Swatches
                product={product}
                colors={colors}
                activeVariant={activeVariant}
                variantSwitch={variantSwitch}
                productUrl={productUrl}
              />
            )}
            <div class="ns-quick-atc" onClick={showProductDetailsEvent}>
              Quick Shop
            </div>
          </div>
          <div class="ns-clipped ns-text-four-lines ns-text-md-three-lines ns-mt-3 ns-mb-2 ns-font-6 ns-product-title ns-font-sofia">
            <AutocompleteElement
              as="a"
              hit={{url: productUrl}}
              class="ns-display-block ns-product-url"
            >{product.name}</AutocompleteElement>
          </div>
          { productPrices.min && (<div class="ns-font-6 ns-font-sofia ns-product-price">
              <div class="ns-price">
                <span>{formatCurrency(productPrices.min)}</span>
                {/* { productPrices.max && productPrices.max > productPrices.min && (
                                    <span class="ns-color-black ns-font-6 ns-ml-2">
                                    - {formatCurrency(productPrices.max)}
                                    </span>
                                )} */}
              </div>
              { productPrices.compare_at_price > 0 && (
                <div class="ns-color-grey-shadow ns-font-6 ns-text-striked ns-ml-2">
                  {formatCurrency(productPrices.compare_at_price)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
